import { useEffect, useMemo, useState } from 'react';
import { logError } from 'shared/helpers/Logger';
import { tryParseJSON } from 'shared/utils/DataUtils';
import { deleteCookie, getCookie, setCookie } from './StorageHelpers';

type StorageSubscriber = () => void;

export class Store {
  protected static instance: undefined | Store;

  protected static getInstance() {
    if (this.instance == null) this.instance = new Store();
    return this.instance;
  }

  static get(key: string) {
    return this.getInstance().get(key);
  }

  static set(
    key: string,
    input: null | string | ((current: null | string) => null | string),
  ): void {
    const storage = this.getInstance();
    if (typeof input == 'function') input = input(storage.get(key));
    storage.update(key, input);
  }

  static delete(key: string): void {
    this.getInstance().update(key, null);
  }

  static subscribe(subscriber: StorageSubscriber): () => void {
    const storage = this.getInstance();
    storage.subscribers.add(subscriber);

    try {
      subscriber();
    } catch (error: unknown) {
      logError(error, 'Storage');
    }

    return () => {
      storage.subscribers.delete(subscriber);
    };
  }

  static subscribeTo(
    key: string,
    subscriber: (value: null | string) => void,
  ): () => void {
    let previous = this.get(key);

    return this.subscribe(() => {
      const current = this.get(key);
      if (!Object.is(previous, current)) {
        previous = current;
        subscriber(current);
      }
    });
  }

  private memory = new Map<string, string>();

  protected subscribers = new Set<StorageSubscriber>();

  protected set(key: string, value: string): void {
    this.memory.set(key, value);
  }

  protected get(key: string): string | null {
    return this.memory.get(key) ?? null;
  }

  protected delete(key: string): void {
    this.memory.delete(key);
  }

  protected emitChange() {
    for (const subscriber of this.subscribers) {
      try {
        subscriber();
      } catch (error: unknown) {
        logError(error, 'Storage');
      }
    }
  }

  protected update(key: string, value: null | string) {
    if (value == null) this.delete(key);
    else this.set(key, value);
    this.emitChange();
  }
}

export class LocalStore extends Store {
  protected static getInstance() {
    if (this.instance == null) {
      try {
        localStorage.getItem('test');
        localStorage.setItem('test', '');
        localStorage.removeItem('test');
        this.instance = new LocalStore();
      } catch {
        this.instance = new Store();
      }
    }

    return this.instance;
  }

  constructor() {
    super();

    window.addEventListener('storage', () => {
      this.emitChange();
    });
  }

  protected set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      super.set(key, value);
    }
  }

  protected get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return super.get(key);
    }
  }

  protected delete(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      super.delete(key);
    }
  }
}

export class SessionStore extends Store {
  protected static getInstance() {
    if (this.instance == null) {
      try {
        sessionStorage.setItem('test', '');
        sessionStorage.removeItem('test');
        this.instance = new SessionStore();
      } catch {
        this.instance = new Store();
      }
    }

    return this.instance;
  }

  protected set(key: string, value: string): void {
    sessionStorage.setItem(key, value);
  }

  protected get(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  protected delete(key: string): void {
    sessionStorage.removeItem(key);
  }
}

export class CookieStore extends Store {
  protected static getInstance() {
    if (this.instance == null) {
      try {
        setCookie('test', '');
        deleteCookie('test');
        this.instance = new CookieStore();
      } catch {
        this.instance = new Store();
      }
    }

    return this.instance;
  }

  protected set(key: string, value: string): void {
    setCookie(key, value, {
      // Same options as backend
      path: '/',
      expires: 14,
      secure: window.location.protocol === 'https:', // for local development
      sameSite: 'Strict',
    });
    this.emitChange();
  }

  protected get(key: string): string | null {
    return getCookie(key) ?? null;
  }

  protected delete(key: string): void {
    deleteCookie(key);
    this.emitChange();
  }
}

export function useLocalStore<T extends null | string = null>(
  key: string,
  defaultValue?: T,
): T | string {
  const [value, setValue] = useState(() => LocalStore.get(key));
  useEffect(() => LocalStore.subscribeTo(key, setValue), [key]);
  return (value ?? defaultValue) as T;
}

export function useSessionStore<T extends null | string = null>(
  key: string,
  defaultValue?: T,
): T | string {
  const [value, setValue] = useState(() => SessionStore.get(key));
  useEffect(() => SessionStore.subscribeTo(key, setValue), [key]);
  return (value ?? defaultValue) as T;
}

export function useJSONLocalStore<T>(localStoreKey: string, defaultValue: T) {
  const value = useLocalStore(localStoreKey);

  return useMemo(() => {
    const storeValue = value ? (JSON.parse(value) as T) : defaultValue;

    const updateStoreValue = (update: T | ((prev: T) => T)) => {
      LocalStore.set(
        localStoreKey,
        JSON.stringify(
          update instanceof Function ? update(storeValue) : update,
        ),
      );
    };

    return { storeValue, updateStoreValue };
  }, [value, defaultValue, localStoreKey]);
}

interface StorageManager {
  get: (key: string) => string | null;
  set: (key: string, value: string) => void;
  delete: (key: string) => void;
}

export function createStorageManager<T>(
  storage: StorageManager,
  storageKey: string,
) {
  return {
    set: (data: T | undefined, expires?: number): void => {
      if (data === undefined) {
        storage.delete(storageKey);
      } else {
        const storageData = {
          data,
          expirationTime: expires ? Date.now() + expires : undefined,
        };

        storage.set(storageKey, JSON.stringify(storageData));
      }
    },
    get: (): T | undefined => {
      const value = storage.get(storageKey);
      if (!value) return undefined;

      const parsed = tryParseJSON<{ data: T; expirationTime?: number } | T>(
        value,
      );

      if (parsed && typeof parsed === 'object' && 'data' in parsed) {
        const structuredData = parsed as { data: T; expirationTime?: number };

        if (
          (structuredData.expirationTime &&
            Date.now() > structuredData.expirationTime) ||
          !structuredData.data
        ) {
          storage.delete(storageKey);
          return undefined;
        }

        return structuredData.data;
      }

      return parsed;
    },
    delete: (): void => {
      storage.delete(storageKey);
    },
  };
}

export function createLocalStorageManager<T>(storageKey: string) {
  return createStorageManager<T>(
    {
      get: (key: string) => LocalStore.get(key),
      set: (key: string, value: string) => {
        LocalStore.set(key, value);
      },
      delete: (key: string) => {
        LocalStore.delete(key);
      },
    },
    storageKey,
  );
}
